import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StatusBadge from '@/components/base/StatusBadge';
import SectionTitle from '@/components/base/SectionTitle';
import { usePageSeo } from '@/hooks/usePageSeo';
import { categoryMap } from '@/mocks/categories';
import { topics } from '@/mocks/topics';

const archiveStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '辟谣档案｜瓜田求证社',
  url: 'https://xinnanwangluo.cn/archive',
  inLanguage: 'zh-CN',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xinnanwangluo.cn/' },
      { '@type': 'ListItem', position: 2, name: '辟谣档案', item: 'https://xinnanwangluo.cn/archive' },
    ],
  },
};

export default function Archive() {
  usePageSeo({
    title: '辟谣档案 | 已被核实不成立的网络爆料归档 - 瓜田求证社',
    description:
      '辟谣档案收录我们已核实为不成立的网络爆料，按时间归档。每条都附上原始说法、证伪依据与来源，方便你在转发前先核对一次。',
    keywords: '网络爆料辟谣,辟谣档案,假消息核实,网络爆料真假,谣言核对',
    path: '/archive',
    structuredData: archiveStructuredData,
  });

  const debunked = topics
    .filter((topic) => topic.status === 'debunked')
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  const groups = debunked.reduce<Record<string, typeof debunked>>((acc, topic) => {
    const year = topic.date.slice(0, 4);
    if (!acc[year]) acc[year] = [];
    acc[year].push(topic);
    return acc;
  }, {});
  const years = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: '辟谣档案' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">辟谣档案</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            这些说法已经被核实为不成立。我们把它们单独归档，不是为了挂人，而是希望下一次同样的截图出现时，
            有人能先搜到这里。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 font-label text-xs text-foreground-700">
              <i className="ri-archive-2-line text-primary-600"></i>
              已归档 {debunked.length} 条
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 font-label text-xs text-foreground-700">
              <i className="ri-history-line text-accent-600"></i>
              最早可追溯至 {years[years.length - 1] ?? '—'} 年
            </span>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          {years.map((year) => (
            <div key={year} className="mb-12 last:mb-0">
              <div className="flex items-center gap-4">
                <span className="font-heading text-3xl font-black text-background-400">{year}</span>
                <span className="h-px flex-1 bg-background-200"></span>
                <span className="font-label text-xs text-foreground-500">{groups[year].length} 条辟谣</span>
              </div>

              <div className="mt-6 space-y-5">
                {groups[year].map((topic) => (
                  <article
                    key={topic.id}
                    className="flex flex-col gap-4 rounded-xl border border-background-200 bg-background-100/50 p-5 transition-colors hover:border-primary-300 sm:flex-row"
                  >
                    <Link
                      to={`/topic/${topic.id}`}
                      className="h-40 w-full flex-none overflow-hidden rounded-lg sm:h-28 sm:w-40"
                      aria-label={topic.title}
                    >
                      <img src={topic.cover} alt={topic.coverAlt} className="h-full w-full object-cover object-top" />
                    </Link>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={topic.status} withShort />
                        <span className="font-label text-xs text-foreground-500">{categoryMap[topic.categoryId]?.name}</span>
                        <span className="font-label text-xs text-foreground-500">{topic.date}</span>
                      </div>
                      <h2 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                        <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
                          {topic.title}
                        </Link>
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-700">{topic.conclusion}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-4 rounded-xl border border-secondary-200 bg-secondary-50 p-5">
            <SectionTitle title="没找到你想核对的传言？" />
            <p className="mt-3 text-sm leading-relaxed text-secondary-900">
              如果一条爆料还没进入档案，可能是我们尚未覆盖，也可能正在求证中。你可以先去求证专区看看进展，
              或直接把线索提交给我们。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/topics"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-list-check-3"></i>
                前往求证专区
              </Link>
              <Link
                to="/about#contact"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-5 py-2.5 font-label text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-send-plane-line"></i>
                提交线索
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}